import React, { useState, useMemo } from 'react';
import {
  Search, ChevronDown, ChevronRight, Type, Hash, DollarSign, Calendar,
  List, User, Percent, Columns3, Layers, Sigma, Check, GripVertical, Database,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { FIELD_CATEGORY_LABELS } from '../constants';

const TYPE_ICONS = {
  text: Type, number: Hash, currency: DollarSign, date: Calendar,
  select: List, user: User, percent: Percent,
};

const NUMERIC_TYPES = new Set(['number', 'currency', 'percent']);

/**
 * Left-pane field catalog.
 *
 * Two render modes:
 *
 *   1. Single-object (legacy):
 *        <FieldsPanel object={normalisedObject} ... />
 *      Shows the object's fields bucketed by fieldCategory
 *      (standard / custom / formula / related). One-object reports.
 *
 *   2. Multi-object (relationship wedge):
 *        <FieldsPanel groups={[{object_key, object_label, prefix,
 *                               is_primary, direction, fields:[...]}, ...]} ... />
 *      Renders an "Account Fields / Lead Fields / Contact Fields"
 *      Salesforce-style sidebar where every group is collapsible and
 *      the primary object's group is pinned open by default. Field
 *      `key` already carries the "<related_key>." prefix from the
 *      backend so columns/filters/groupBy values flow through to the
 *      engine without further mangling.
 *
 * If both `groups` and `object` are passed, `groups` wins (the report
 * type carries multi-object info; the per-object normalisation is
 * already merged into the group structure).
 */
const FieldsPanel = ({
  object, groups, config,
  onAddColumn, onAddGroup, onAddSummary, onDragField,
}) => {
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState({});

  // Mode detection: explicit groups[] beats single-object fallback.
  const isMultiObject = Array.isArray(groups) && groups.length > 0;

  // Normalise to a common `sections[]` shape so the render loop below
  // only deals with one structure. Each section has:
  //   { id, title, fields, headerIcon, isPrimary }
  const sections = useMemo(() => {
    const filt = (f) => {
      if (!search) return true;
      const q = search.toLowerCase();
      const label = (f.label || '').toLowerCase();
      const key = (f.key || '').toLowerCase();
      return label.includes(q) || key.includes(q);
    };

    if (isMultiObject) {
      // Sort: primary first, then alphabetical by object label.
      const sorted = [...groups].sort((a, b) => {
        if (a.is_primary !== b.is_primary) return a.is_primary ? -1 : 1;
        return (a.object_label || a.object_key || '').localeCompare(
          b.object_label || b.object_key || '',
        );
      });
      return sorted.map((g) => ({
        id: `obj/${g.object_key}`,
        title: `${g.object_label} Fields`,
        subtitle: g.is_primary ? 'Primary' : (
          g.direction === 'child' ? 'Related (1:N)'
            : g.direction === 'parent' ? 'Related (1:1)'
              : 'Related'
        ),
        fields: (g.fields || []).filter(filt),
        isPrimary: !!g.is_primary,
      }));
    }

    // Single-object fallback: bucket by fieldCategory (the old
    // standard / custom / formula / related grouping).
    const buckets = { standard: [], custom: [], formula: [], related: [] };
    (object?.fields || []).filter(filt).forEach((f) => {
      const cat = f.fieldCategory || 'standard';
      (buckets[cat] = buckets[cat] || []).push(f);
    });
    return Object.entries(buckets)
      .filter(([, fields]) => fields.length > 0)
      .map(([cat, fields]) => ({
        id: `cat/${cat}`,
        title: FIELD_CATEGORY_LABELS[cat] || cat,
        subtitle: null,
        fields,
        isPrimary: false,
      }));
  }, [isMultiObject, groups, object, search]);

  const toggle = (id) => setCollapsed((c) => ({ ...c, [id]: !c[id] }));

  const headerLabel = isMultiObject
    ? (groups.find((g) => g.is_primary)?.object_label || 'Report') + ' Fields'
    : `${object?.label || 'Object'} Fields`;

  // Lookups for the "already in this drop zone" check marks. Works
  // identically in both modes — the prefixed keys are the same as
  // what gets stored in config.columns / config.groupBy.
  const usedColumns = config?.columns || [];
  const usedGroupBy = config?.groupBy || [];

  return (
    <div className="flex flex-col h-full">
      <div className="px-3 py-3 border-b border-slate-200">
        <h3 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-1.5">
          <Columns3 className="h-4 w-4 text-indigo-600" /> {headerLabel}
        </h3>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search fields..." className="pl-8 h-8 text-sm" />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-2">
        {sections.map((section) => {
          if (!section.fields.length) return null;
          const isCollapsed = collapsed[section.id] === true
            // Default: primary open, others collapsed in multi-object mode
            // so a 5-object catalog doesn't unfurl 200 fields at once.
            || (collapsed[section.id] === undefined && isMultiObject && !section.isPrimary);
          return (
            <div key={section.id} className="mb-1">
              <button onClick={() => toggle(section.id)}
                className="w-full flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wide hover:text-slate-700">
                {isCollapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                {isMultiObject && (
                  <Database className={`h-3 w-3 ${section.isPrimary ? 'text-indigo-500' : 'text-slate-400'}`} />
                )}
                <span className="truncate">{section.title}</span>
                {section.subtitle && (
                  <span className="ml-1 text-[10px] font-normal normal-case text-slate-400">
                    {section.subtitle}
                  </span>
                )}
                <span className="ml-auto text-slate-400 font-normal">{section.fields.length}</span>
              </button>
              {!isCollapsed && section.fields.map((field) => {
                const Icon = TYPE_ICONS[field.type] || Type;
                const inColumns = usedColumns.includes(field.key);
                const inGroup = usedGroupBy.includes(field.key);
                const isNumeric = NUMERIC_TYPES.has(field.type);
                return (
                  <div
                    key={field.key}
                    draggable
                    onDragStart={() => onDragField && onDragField(field)}
                    onClick={() => onAddColumn && onAddColumn(field.key)}
                    className="group flex items-center gap-2 px-3 py-1.5 mx-1.5 rounded-md hover:bg-indigo-50 cursor-grab active:cursor-grabbing"
                    title="Drag to a drop zone, or click to add as a column"
                  >
                    <GripVertical className="h-3.5 w-3.5 text-slate-300 group-hover:text-slate-400" />
                    <Icon className="h-3.5 w-3.5 text-indigo-400 flex-shrink-0" />
                    <span className="text-sm text-slate-700 flex-1 truncate">{field.label}</span>
                    {(inColumns || inGroup) && (
                      <Check className="h-3.5 w-3.5 text-emerald-500" />
                    )}
                    <div className="hidden group-hover:flex items-center gap-0.5">
                      <button
                        onClick={(e) => { e.stopPropagation(); onAddGroup && onAddGroup(field.key); }}
                        className="p-1 rounded hover:bg-indigo-100 text-slate-400 hover:text-indigo-600"
                        title="Add to Group By"
                      >
                        <Layers className="h-3 w-3" />
                      </button>
                      {isNumeric && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onAddSummary && onAddSummary(field.key); }}
                          className="p-1 rounded hover:bg-indigo-100 text-slate-400 hover:text-indigo-600"
                          title="Add to Summarize"
                        >
                          <Sigma className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <div className="px-3 py-2 border-t border-slate-200 bg-slate-50 text-[11px] text-slate-400 leading-snug">
        Drag a field onto Columns, Group By or Summarize — or click to add as a column.
      </div>
    </div>
  );
};

export default FieldsPanel;
